import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { api, API_BASE, errorMessage } from "../../lib/api";
import { Download, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SurveyAnalytics() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [topupOpen, setTopupOpen] = useState(false);
    const [delta, setDelta] = useState(50);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [events, setEvents] = useState([]);
    const [wsLive, setWsLive] = useState(false);
    const wsRef = useRef(null);

    const load = useCallback(() => api.get(`/surveys/${id}/analytics`).then((r) => setData(r.data)), [id]);
    const loadEvents = useCallback(() => api.get(`/surveys/${id}/events`).then((r) => setEvents(r.data || [])).catch(() => setEvents([])), [id]);
    useEffect(() => { load(); loadEvents(); }, [load, loadEvents]);

    // Live activity feed via WebSocket. Subscribes on mount, prepends new events,
    // and de-dupes against existing event IDs from the initial REST load.
    useEffect(() => {
        if (!id) return undefined;
        const httpBase = API_BASE.replace(/\/api$/, "");
        const wsBase = httpBase.replace(/^http/, "ws");
        const url = `${wsBase}/api/ws/surveys/${id}/events`;
        let ws;
        let stopped = false;
        let retryTimer;

        const connect = () => {
            if (stopped) return;
            try {
                ws = new WebSocket(url);
            } catch {
                retryTimer = setTimeout(connect, 4000);
                return;
            }
            wsRef.current = ws;
            ws.onopen = () => setWsLive(true);
            ws.onclose = () => {
                setWsLive(false);
                if (!stopped) retryTimer = setTimeout(connect, 4000);
            };
            ws.onerror = () => { /* close handler will retry */ };
            ws.onmessage = (msg) => {
                try {
                    const payload = JSON.parse(msg.data);
                    if (payload.type !== "event" || !payload.event) return;
                    const ev = payload.event;
                    setEvents((prev) => (prev.some((p) => p.id === ev.id) ? prev : [ev, ...prev]));
                } catch {
                    /* ignore bad frames */
                }
            };
        };

        connect();
        return () => {
            stopped = true;
            if (retryTimer) clearTimeout(retryTimer);
            if (ws && ws.readyState <= 1) ws.close();
            wsRef.current = null;
            setWsLive(false);
        };
    }, [id]);

    useEffect(() => {
        if (!topupOpen || !delta || delta < 1) { setPreview(null); return; }
        let cancelled = false;
        api.post(`/surveys/${id}/self-extend/preview`, {
            responses_needed_delta: Number(delta),
            quota_extensions: [],
        })
            .then((r) => { if (!cancelled) setPreview(r.data.cost_delta_usd); })
            .catch(() => { if (!cancelled) setPreview(null); });
        return () => { cancelled = true; };
    }, [topupOpen, delta, id]);

    const submitTopup = async () => {
        setBusy(true);
        try {
            // Real Stripe Checkout (test mode). Backend recomputes cost from
            // the survey row; we just pass the delta and origin_url.
            const { data: res } = await api.post(`/payments/checkout/extend/${id}`, {
                responses_needed_delta: Number(delta),
                origin_url: window.location.origin,
            });
            if (!res?.url) throw new Error("Checkout URL missing");
            window.location.href = res.url;
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (!data) return <div className="min-h-screen flex items-center justify-center text-xs font-mono">LOADING…</div>;
    const s = data.survey;
    const canTopUp = ["live", "paused", "completed"].includes(s.status);

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
                <p className="eyebrow mb-2 text-advertiser">{s.status.toUpperCase()} · {s.category}</p>
                <h1 className="h2 mb-2" data-testid="analytics-title">{s.title}</h1>
                <p className="text-ink-600 text-sm mb-8">{s.description}</p>

                <div className="grid sm:grid-cols-4 gap-0 border border-ink-200 mb-10">
                    <Stat label="Started" value={data.started} />
                    <Stat label="Completed" value={data.completed} />
                    <Stat label="Completion rate" value={`${data.completion_rate_pct}%`} />
                    <Stat label="Avg. time" value={`${data.avg_time_seconds}s`} />
                </div>

                {(data.disqualified > 0 || data.invalid > 0) && (
                    <div className="grid sm:grid-cols-2 gap-0 border border-ink-200 mb-10" data-testid="antifraud-stats">
                        <div className="p-6 border-r border-ink-200">
                            <div className="font-mono text-3xl">{data.disqualified || 0}</div>
                            <div className="text-xs uppercase tracking-wider text-ink-600 mt-1">SCREENED OUT (disqualify rules)</div>
                        </div>
                        <div className="p-6">
                            <div className="font-mono text-3xl text-error">{data.invalid || 0}</div>
                            <div className="text-xs uppercase tracking-wider text-ink-600 mt-1">REJECTED (anti-fraud)</div>
                            {data.invalid_reasons && Object.keys(data.invalid_reasons).length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1" data-testid="antifraud-reasons">
                                    {Object.entries(data.invalid_reasons).map(([k, v]) => (
                                        <span key={k} className="font-mono text-[10px] border border-ink-300 px-2 py-0.5">
                                            {k} · {v}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 mb-4 flex-wrap">
                    {canTopUp && (
                        <button
                            onClick={() => setTopupOpen((v) => !v)}
                            className="btn btn-advertiser text-sm"
                            data-testid="top-up-btn"
                        >
                            <Plus size={16} /> {topupOpen ? "Cancel top-up" : "Top up survey"}
                        </button>
                    )}
                    <a href={`${API_BASE}/surveys/${id}/responses.csv`} className="btn btn-outline text-sm" data-testid="analytics-csv-btn"
                       download>
                        <Download size={16} /> Export CSV
                    </a>
                </div>

                {topupOpen && (
                    <div className="card-base p-6 mb-10" data-testid="top-up-panel">
                        <p className="eyebrow mb-3">SELF-SERVE TOP-UP</p>
                        <p className="text-xs text-ink-600 mb-4">
                            Add capacity instantly — billed to your card via Stripe. Currently {s.completed_count}/{s.responses_needed} responses · ${s.advertiser_cost_usd.toFixed(2)} spent.
                        </p>
                        <div className="grid sm:grid-cols-3 gap-4 items-end">
                            <div className="sm:col-span-1">
                                <label className="eyebrow block mb-2">Add responses</label>
                                <input
                                    type="number" min={1}
                                    className="input-base"
                                    value={delta}
                                    onChange={(e) => setDelta(e.target.value)}
                                    data-testid="top-up-delta-input"
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <div className="eyebrow mb-2">Live total</div>
                                <div className="font-mono text-2xl" data-testid="top-up-cost-preview">
                                    {preview != null ? `$${preview.toFixed(2)}` : "—"}
                                </div>
                                <div className="text-[10px] font-mono text-ink-600 mt-1">STRIPE CHECKOUT (MOCKED)</div>
                            </div>
                            <button
                                onClick={submitTopup}
                                disabled={busy || !delta || delta < 1}
                                className="btn btn-advertiser sm:col-span-1"
                                data-testid="top-up-submit-btn"
                            >
                                {busy ? "Charging…" : "Pay & extend"}
                            </button>
                        </div>
                    </div>
                )}

                {events.length > 0 && (
                    <section className="mb-10" data-testid="activity-feed">
                        <div className="flex items-center justify-between mb-3">
                            <p className="eyebrow">ACTIVITY</p>
                            <span
                                className={`font-mono text-[10px] px-2 py-1 border ${wsLive ? "border-success text-success" : "border-ink-300 text-ink-600"}`}
                                data-testid="activity-feed-live-badge"
                                data-live={wsLive ? "1" : "0"}
                            >
                                {wsLive ? "● LIVE" : "○ OFFLINE"}
                            </span>
                        </div>
                        <div className="card-base">
                            {events.slice(0, 25).map((e) => (
                                <div key={e.id} className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-ink-200 last:border-b-0 text-sm" data-testid={`event-${e.id}`}>
                                    <div className="col-span-3 font-mono text-xs text-ink-600">{new Date(e.created_at).toLocaleString()}</div>
                                    <div className="col-span-3 font-mono text-xs">{e.type.toUpperCase()}</div>
                                    <div className="col-span-6">{e.summary}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <p className="eyebrow mb-3">PER QUESTION</p>
                <div className="space-y-4 mb-12">
                    {data.questions.map((q) => (
                        <div key={q.id} className="card-base p-5" data-testid={`analytics-q-${q.id}`}>
                            <div className="font-heading font-bold mb-3">{q.text}</div>
                            <div className="text-xs text-ink-600 font-mono mb-3">{q.type.toUpperCase()}</div>
                            {Object.keys(q.buckets).length > 0 ? (
                                <Bars items={q.buckets} />
                            ) : q.texts.length > 0 ? (
                                <div className="space-y-1">
                                    {q.texts.slice(0, 10).map((t, i) => (
                                        <div key={i} className="text-sm border-l-2 border-ink-200 pl-3 py-1 text-ink-600">{t}</div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-ink-600">No responses yet.</div>
                            )}
                        </div>
                    ))}
                </div>

                <p className="eyebrow mb-3">DEMOGRAPHIC BREAKDOWN</p>
                <div className="grid md:grid-cols-2 gap-4 mb-12">
                    {Object.entries(data.demographics).map(([k, buckets]) => (
                        <div key={k} className="card-base p-5">
                            <div className="eyebrow mb-3">{k.toUpperCase()}</div>
                            {Object.keys(buckets).length === 0 ? <div className="text-sm text-ink-600">No data</div> : <Bars items={buckets} />}
                        </div>
                    ))}
                </div>

                {(s.quotas || []).length > 0 && (
                    <>
                        <p className="eyebrow mb-3">QUOTA BUCKETS</p>
                        <div className="card-base">
                            {s.quotas.map((q) => {
                                const pct = Math.min(100, Math.round(((q.current_count || 0) / q.max_responses) * 100));
                                const full = (q.current_count || 0) >= q.max_responses;
                                return (
                                    <div key={q.id} className="px-5 py-4 border-b border-ink-200 last:border-b-0" data-testid={`quota-stat-${q.id}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-semibold text-sm">{q.name || q.id}</span>
                                            <span className="font-mono text-xs">{q.current_count || 0} / {q.max_responses} {full ? "· FULL" : ""}</span>
                                        </div>
                                        <div className="h-2 bg-ink-100">
                                            <div className={`h-full ${full ? "bg-success" : "bg-advertiser"}`} style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="p-6 border-r border-ink-200 last:border-r-0">
            <div className="font-mono text-3xl">{value}</div>
            <div className="text-xs uppercase tracking-wider text-ink-600 mt-1">{label}</div>
        </div>
    );
}

function Bars({ items }) {
    const total = Object.values(items).reduce((a, b) => a + b, 0) || 1;
    const sorted = Object.entries(items).sort((a, b) => b[1] - a[1]);
    return (
        <div className="space-y-2">
            {sorted.map(([k, v]) => (
                <div key={k}>
                    <div className="flex justify-between text-xs mb-1">
                        <span className="truncate pr-2">{k}</span>
                        <span className="font-mono">{v} ({Math.round((v / total) * 100)}%)</span>
                    </div>
                    <div className="h-2 bg-ink-100">
                        <div className="h-full bg-advertiser" style={{ width: `${(v / total) * 100}%` }} />
                    </div>
                </div>
            ))}
        </div>
    );
}
